import type { CreatorSiteThemePreset } from './creator-site';
import type { CreatorSiteSectionInput } from './creator-site-sections';
import type { CreatorSiteProfileSeedSource } from './creator-site-profile-seed';
import { buildCreatorSiteProfileSeed } from './creator-site-profile-seed';
import { CREATOR_SITE_SECTION_VISIBILITY_DEFAULT } from './creator-site-visibility';

export const CREATOR_SITE_NICHE_TEMPLATE_IDS = ['coach', 'creator', 'educator', 'agency', 'speaker'] as const;

export type CreatorSiteNicheTemplateId = (typeof CREATOR_SITE_NICHE_TEMPLATE_IDS)[number];

export type CreatorSiteNicheTemplatePage = {
  slug: string;
  title: string;
  seoTitle?: string | null;
  seoDescription?: string | null;
  sections: CreatorSiteSectionInput[];
};

export type CreatorSiteNicheTemplateDefinition = {
  id: CreatorSiteNicheTemplateId;
  label: string;
  description: string;
  themePreset: CreatorSiteThemePreset;
  homepageSections: CreatorSiteSectionInput[];
  subpages: CreatorSiteNicheTemplatePage[];
};

export type CreatorSiteNicheTemplateApplication = {
  templateId: CreatorSiteNicheTemplateId;
  themePreset: CreatorSiteThemePreset;
  siteTitle: string;
  seoTitle: string;
  seoDescription: string | null;
  homepageSections: CreatorSiteSectionInput[];
  subpages: CreatorSiteNicheTemplatePage[];
};

function section(
  type: CreatorSiteSectionInput['type'],
  content: Record<string, unknown>,
  options?: { bindings?: Record<string, unknown> | null },
): CreatorSiteSectionInput {
  return {
    type,
    variant: 'default',
    visible: true,
    visibility: CREATOR_SITE_SECTION_VISIBILITY_DEFAULT,
    content,
    bindings: options?.bindings ?? null,
  };
}

const COACH_TEMPLATE: CreatorSiteNicheTemplateDefinition = {
  id: 'coach',
  label: 'Coach',
  description: 'Book sessions, show proof, and capture inquiries.',
  themePreset: 'coach',
  homepageSections: [
    section('navbar', {}),
    section('hero', {
      headline: 'Transform your results',
      subheadline: '1:1 coaching for ambitious creators and founders.',
      ctaLabel: 'Book a call',
      ctaHref: '#contact',
    }),
    section('about', {
      headline: 'How I help',
      body: 'Share your coaching philosophy, who you work with, and the outcomes clients can expect.',
    }),
    section('testimonials', {
      headline: 'Client wins',
      items: [
        {
          quote: 'Working together doubled my launch revenue in 90 days.',
          author: 'Jordan M.',
          role: 'Course creator',
          source: 'Client email',
        },
      ],
    }),
    section('case_study', {
      headline: 'Case studies',
      items: [
        {
          title: 'From stuck to sold out',
          outcome: 'Filled a cohort in 14 days',
          summary: 'How we repositioned the offer and simplified the funnel.',
          pageSlug: 'results',
          ctaLabel: 'Read results',
        },
      ],
    }),
    section('faq', {
      items: [
        {
          question: 'Who is this for?',
          answer: 'Creators and founders ready to invest in structured coaching.',
        },
        {
          question: 'How do sessions work?',
          answer: 'Weekly 1:1 calls plus async support between sessions.',
        },
      ],
    }),
    section('contact_form', {
      headline: 'Start a conversation',
      buttonLabel: 'Send message',
      inquiryTypes: ['Coaching', 'Speaking', 'General inquiry'],
    }),
    section('footer', {}),
  ],
  subpages: [
    {
      slug: 'results',
      title: 'Results',
      seoTitle: 'Client results',
      seoDescription: 'Outcomes from recent coaching engagements.',
      sections: [
        section('navbar', {}),
        section('rich_text', {
          body: 'Share detailed client stories, metrics, and before/after snapshots.',
        }),
        section('testimonials', {
          headline: 'More proof',
          items: [
            {
              quote: 'The clarity alone was worth it.',
              author: 'Sam K.',
              role: 'Founder',
              source: 'LinkedIn DM',
            },
          ],
        }),
        section('contact_form', {
          headline: 'Ready to work together?',
          buttonLabel: 'Get in touch',
          inquiryTypes: ['Coaching'],
        }),
        section('footer', {}),
      ],
    },
  ],
};

const CREATOR_TEMPLATE: CreatorSiteNicheTemplateDefinition = {
  id: 'creator',
  label: 'Creator',
  description: 'Showcase offers, content, and grow your list.',
  themePreset: 'creator',
  homepageSections: [
    section('navbar', {}),
    section('hero', {
      headline: 'Build with me',
      subheadline: 'Courses, templates, and tools for growing creators.',
      ctaLabel: 'See offers',
      ctaHref: '#offers',
    }),
    section('about', {
      headline: 'About',
      body: 'Introduce your brand, niche, and what followers can expect from you.',
    }),
    section('social_links', {}),
    section('offer_grid', {}, { bindings: { offerIds: [] } }),
    section('article_list', {}, { bindings: { limit: 3 } }),
    section('newsletter_signup', {
      headline: 'Join the list',
      buttonLabel: 'Subscribe',
    }),
    section('follow_loamify', {
      headline: 'Follow on Loamify',
      buttonLabel: 'Follow',
    }),
    section('footer', {}),
  ],
  subpages: [
    {
      slug: 'work-with-me',
      title: 'Work with me',
      seoTitle: 'Work with me',
      seoDescription: 'Collaborations, sponsorships, and custom projects.',
      sections: [
        section('navbar', {}),
        section('rich_text', {
          body: 'Outline collaboration types, rates or ranges, and how to get started.',
        }),
        section('contact_form', {
          headline: 'Pitch a project',
          buttonLabel: 'Send inquiry',
          inquiryTypes: ['Brand deal', 'Custom project', 'Other'],
        }),
        section('footer', {}),
      ],
    },
  ],
};

const EDUCATOR_TEMPLATE: CreatorSiteNicheTemplateDefinition = {
  id: 'educator',
  label: 'Educator',
  description: 'Promote courses, answer questions, and build trust.',
  themePreset: 'editorial',
  homepageSections: [
    section('navbar', {}),
    section('hero', {
      headline: 'Learn with clarity',
      subheadline: 'Practical courses and workshops for serious students.',
      ctaLabel: 'Browse courses',
      ctaHref: '#courses',
    }),
    section('about', {
      headline: 'Teaching approach',
      body: 'Explain your expertise, teaching style, and who your programs are for.',
    }),
    section('course_showcase', {}, { bindings: { courseIds: [] } }),
    section('faq', {
      items: [
        {
          question: 'What level is this for?',
          answer: 'Programs are designed for motivated beginners through intermediate learners.',
        },
        {
          question: 'Do you offer certificates?',
          answer: 'Completion certificates are included with supported programs.',
        },
      ],
    }),
    section('testimonials', {
      headline: 'Student feedback',
      items: [
        {
          quote: 'Finally a course that respects my time and gets to the point.',
          author: 'Priya S.',
          role: 'Student',
          source: 'Course survey',
        },
      ],
    }),
    section('newsletter_signup', {
      headline: 'Get new lesson drops',
      buttonLabel: 'Subscribe',
    }),
    section('footer', {}),
  ],
  subpages: [
    {
      slug: 'courses',
      title: 'Courses',
      seoTitle: 'Courses',
      seoDescription: 'Programs and learning paths from this educator.',
      sections: [
        section('navbar', {}),
        section('rich_text', {
          body: 'Summarize your catalog, prerequisites, and enrollment process.',
        }),
        section('course_showcase', {}, { bindings: { courseIds: [] } }),
        section('faq', {
          items: [
            {
              question: 'How do enrollments work?',
              answer: 'Choose a program, checkout, and get instant access where available.',
            },
          ],
        }),
        section('footer', {}),
      ],
    },
  ],
};

const AGENCY_TEMPLATE: CreatorSiteNicheTemplateDefinition = {
  id: 'agency',
  label: 'Agency',
  description: 'Showcase services, case studies, and capture qualified leads.',
  themePreset: 'bold',
  homepageSections: [
    section('navbar', {}),
    section('hero', {
      headline: 'We grow brands that stand out',
      subheadline: 'Strategy, creative, and performance for ambitious teams.',
      ctaLabel: 'Book a discovery call',
      ctaHref: '#contact',
    }),
    section('about', {
      headline: 'What we do',
      body: 'Describe your agency specialty, process, and the outcomes clients can expect.',
    }),
    section('case_study', {
      headline: 'Selected work',
      items: [
        {
          title: 'Launch campaign',
          outcome: '+42% qualified leads in 90 days',
          summary: 'Brief overview of the challenge, approach, and measurable results.',
          ctaLabel: 'Read case study',
          pageSlug: 'work',
        },
      ],
    }),
    section('testimonials', {
      headline: 'Client feedback',
      items: [
        {
          quote: 'They moved fast without sacrificing quality.',
          author: 'Taylor R.',
          role: 'Marketing director',
          source: 'Client email',
        },
      ],
    }),
    section('contact_form', {
      headline: 'Start a project',
      buttonLabel: 'Send inquiry',
      inquiryTypes: ['Strategy', 'Creative', 'Performance', 'Other'],
    }),
    section('footer', {}),
  ],
  subpages: [
    {
      slug: 'work',
      title: 'Our work',
      seoTitle: 'Our work',
      sections: [
        section('navbar', {}),
        section('case_study', {
          headline: 'Case studies',
          items: [
            {
              title: 'Brand refresh',
              outcome: '2x inbound demo requests',
              summary: 'Repositioning, site refresh, and launch campaign.',
            },
          ],
        }),
        section('footer', {}),
      ],
    },
  ],
};

const SPEAKER_TEMPLATE: CreatorSiteNicheTemplateDefinition = {
  id: 'speaker',
  label: 'Speaker',
  description: 'Promote keynotes, media, and booking inquiries.',
  themePreset: 'editorial',
  homepageSections: [
    section('navbar', {}),
    section('hero', {
      headline: 'Keynotes that move audiences',
      subheadline: 'Inspiring talks on leadership, creativity, and growth.',
      ctaLabel: 'Book me to speak',
      ctaHref: '#contact',
    }),
    section('about', {
      headline: 'Speaking topics',
      body: 'List signature talks, audiences you serve, and formats (keynote, workshop, panel).',
    }),
    section('embed', {
      title: 'Featured talk',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    }),
    section('testimonials', {
      headline: 'Organizer feedback',
      items: [
        {
          quote: 'Our audience was engaged from the first minute to the last.',
          author: 'Event director',
          role: 'Annual summit',
          source: 'Post-event survey',
        },
      ],
    }),
    section('contact_form', {
      headline: 'Booking inquiry',
      buttonLabel: 'Request availability',
      inquiryTypes: ['Keynote', 'Workshop', 'Virtual', 'Other'],
    }),
    section('footer', {}),
  ],
  subpages: [],
};

export const CREATOR_SITE_NICHE_TEMPLATES: Record<
  CreatorSiteNicheTemplateId,
  CreatorSiteNicheTemplateDefinition
> = {
  coach: COACH_TEMPLATE,
  creator: CREATOR_TEMPLATE,
  educator: EDUCATOR_TEMPLATE,
  agency: AGENCY_TEMPLATE,
  speaker: SPEAKER_TEMPLATE,
};

export function isCreatorSiteNicheTemplateId(value: string): value is CreatorSiteNicheTemplateId {
  return (CREATOR_SITE_NICHE_TEMPLATE_IDS as readonly string[]).includes(value);
}

export function listCreatorSiteNicheTemplates() {
  return CREATOR_SITE_NICHE_TEMPLATE_IDS.map((id) => {
    const template = CREATOR_SITE_NICHE_TEMPLATES[id];
    return {
      id: template.id,
      label: template.label,
      description: template.description,
      themePreset: template.themePreset,
      subpageCount: template.subpages.length,
    };
  });
}

export function buildCreatorSiteNicheTemplateApplication(
  templateId: string,
  profile?: CreatorSiteProfileSeedSource | null,
): { ok: true; value: CreatorSiteNicheTemplateApplication } | { ok: false; message: string } {
  if (!isCreatorSiteNicheTemplateId(templateId)) {
    return { ok: false, message: 'Invalid niche template id' };
  }

  const template = CREATOR_SITE_NICHE_TEMPLATES[templateId];
  const baseHomepageSections = template.homepageSections.map((item) => ({
    ...item,
    content: { ...(item.content as Record<string, unknown>) },
    bindings: item.bindings ?? null,
  }));

  const profileSeed = profile
    ? buildCreatorSiteProfileSeed(profile, baseHomepageSections)
    : {
        siteTitle: template.label,
        seoTitle: template.label,
        seoDescription: template.description,
        sections: baseHomepageSections,
      };

  return {
    ok: true,
    value: {
      templateId: template.id,
      themePreset: template.themePreset,
      siteTitle: profileSeed.siteTitle,
      seoTitle: profileSeed.seoTitle,
      seoDescription: profileSeed.seoDescription,
      homepageSections: profileSeed.sections,
      subpages: template.subpages.map((page) => ({
        ...page,
        sections: page.sections.map((item) => ({
          ...item,
          content: { ...(item.content as Record<string, unknown>) },
          bindings: item.bindings ?? null,
        })),
      })),
    },
  };
}
